/**
 * Element-major track model for the timeline.
 *
 * Reveals are per-element tracks that appear-and-stay (no out-time). The
 * "effect" kinds — annotations, pen/marker strokes, spotlights — are per-item
 * tracks WITH an out-time (a resizable end). Camera moves collapse into ONE
 * track holding multiple blocks. Each block carries a global `[startMs, endMs)`
 * window (endMs null = stays to the end of the slide, used by reveals). Items
 * are stored per-step with local `triggerMs`/`endMs`; dragging re-homes them to
 * the narration segment they land on; resizing sets the end. Playback is
 * unchanged in shape (per-step windows via reveal-timing / camera-playback).
 */

import { snapMsToWord } from 'ai-tutor-player';
import type { RevealStep, ScriptElement, Slide } from '@/lib/schema.types';

import { elementMapOf, stepDurationMs } from './clips';
import { labelForElement } from '../StepTimeline';
import { globalToStep, type StepSpan } from './playback';

export type ItemKind = 'reveal' | 'annotation' | 'camera' | 'draw' | 'spotlight';

/** Default out-time for a freshly-created effect (ms after its trigger). */
export const DEFAULT_EFFECT_MS = 2500;
const MIN_CLIP_MS = 120;

export interface TimelineClip {
  refId: string; // elementId | annotation.id | keyframe.id | stroke.id | spotlight.id
  ownerStepId: string;
  ownerStepIndex: number;
  startMs: number; // global
  endMs: number | null; // global; null = stays to end of slide (reveals)
  label: string;
  color: string;
  bound: boolean;
}

export interface TimelineTrack {
  key: string;
  kind: ItemKind;
  label: string; // gutter label
  color: string;
  clips: TimelineClip[]; // reveal/annotation/draw/spotlight: 0–1; camera: 0–N
  /** Reveal track with no clip yet → click the row to place its reveal. */
  emptyElementId?: string;
}

export interface ItemRef {
  kind: ItemKind;
  refId: string;
}

const REVEAL_COLOR = '#6366f1';
const CAMERA_COLOR = '#0ea5e9';
const SPOTLIGHT_COLOR = '#f59e0b';

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function endGlobal(spans: StepSpan[], k: number, endMs?: number): number | null {
  return endMs != null ? spans[k].start + endMs : null;
}

export function buildTracks(slide: Slide, steps: RevealStep[], spans: StepSpan[]): TimelineTrack[] {
  const elementMap = elementMapOf(slide);
  const tracks: TimelineTrack[] = [];

  // reveals — one per slide element (appear-and-stay)
  const flat: ScriptElement[] = [];
  slide.rows.forEach((r) => r.columns.forEach((c) => c.elements.forEach((e) => flat.push(e))));
  for (const el of flat) {
    let owner = -1;
    for (let k = 0; k < steps.length; k++) {
      if ((steps[k].elementIds ?? []).includes(el.id)) {
        owner = k;
        break;
      }
    }
    const t = owner >= 0 ? steps[owner].elementTimings?.[el.id] : undefined;
    const clips: TimelineClip[] =
      owner >= 0
        ? [
            {
              refId: el.id,
              ownerStepId: steps[owner].id,
              ownerStepIndex: owner,
              startMs: spans[owner].start + (t?.triggerMs ?? 0),
              endMs: null, // reveals stay
              label: labelForElement(el),
              color: REVEAL_COLOR,
              bound: t?.startWordIndex != null,
            },
          ]
        : [];
    tracks.push({
      key: `reveal:${el.id}`,
      kind: 'reveal',
      label: labelForElement(el),
      color: REVEAL_COLOR,
      clips,
      emptyElementId: owner < 0 ? el.id : undefined,
    });
  }

  // annotations — per item, with out-time
  steps.forEach((step, k) => {
    (step.annotations ?? []).forEach((a) => {
      const target = elementMap.get(a.targetElementId);
      const label = `${cap(a.kind)}: ${target ? labelForElement(target) : 'element'}`;
      tracks.push({
        key: `annotation:${a.id}`,
        kind: 'annotation',
        label,
        color: a.color ?? '#facc15',
        clips: [
          {
            refId: a.id,
            ownerStepId: step.id,
            ownerStepIndex: k,
            startMs: spans[k].start + (a.triggerMs ?? 0),
            endMs: endGlobal(spans, k, a.endMs),
            label,
            color: a.color ?? '#facc15',
            bound: a.startWordIndex != null,
          },
        ],
      });
    });
  });

  // drawings — per stroke, with out-time
  steps.forEach((step, k) => {
    (step.drawStrokes ?? []).forEach((s) => {
      const isMarker = (s.strokeWidth ?? 0) >= 10;
      const label = `${isMarker ? 'Marker' : 'Pen'} · ${s.points?.length ?? 0} pts`;
      const e = (s as { endMs?: number }).endMs;
      tracks.push({
        key: `draw:${s.id}`,
        kind: 'draw',
        label,
        color: s.color ?? '#111827',
        clips: [
          {
            refId: s.id,
            ownerStepId: step.id,
            ownerStepIndex: k,
            startMs: spans[k].start + (s.triggerMs ?? 0),
            endMs: endGlobal(spans, k, e),
            label,
            color: s.color ?? '#111827',
            bound: (s as { startWordIndex?: number }).startWordIndex != null,
          },
        ],
      });
    });
  });

  // spotlights — per item, with out-time
  steps.forEach((step, k) => {
    (step.spotlights ?? []).forEach((sp) => {
      const label = `Spotlight (${sp.shape === 'ellipse' ? 'oval' : 'circle'})`;
      tracks.push({
        key: `spotlight:${sp.id}`,
        kind: 'spotlight',
        label,
        color: SPOTLIGHT_COLOR,
        clips: [
          {
            refId: sp.id,
            ownerStepId: step.id,
            ownerStepIndex: k,
            startMs: spans[k].start + (sp.triggerMs ?? 0),
            endMs: endGlobal(spans, k, sp.endMs),
            label,
            color: SPOTLIGHT_COLOR,
            bound: sp.startWordIndex != null,
          },
        ],
      });
    });
  });

  // camera — ONE track holding every keyframe as a block
  const camClips: TimelineClip[] = [];
  steps.forEach((step, k) => {
    (step.cameraKeyframes ?? []).forEach((kf) => {
      const n = kf.labels?.length ?? 0;
      camClips.push({
        refId: kf.id,
        ownerStepId: step.id,
        ownerStepIndex: k,
        startMs: spans[k].start + (kf.triggerMs ?? 0),
        endMs: endGlobal(spans, k, kf.endMs),
        label: n > 0 ? `Camera · ${n} label${n === 1 ? '' : 's'}` : 'Camera move',
        color: CAMERA_COLOR,
        bound: kf.startWordIndex != null,
      });
    });
  });
  if (camClips.length > 0) {
    tracks.push({ key: 'camera', kind: 'camera', label: 'Camera', color: CAMERA_COLOR, clips: camClips });
  }

  return tracks;
}

/** Sorted global word-start times (ms) across the whole slide — the snap grid. */
export function buildGlobalWordStarts(steps: RevealStep[], spans: StepSpan[]): number[] {
  const out: number[] = [];
  steps.forEach((s, k) => {
    (s.audio?.words ?? []).forEach((w) => out.push(Math.round(spans[k].start + w.start * 1000)));
  });
  return out.sort((a, b) => a - b);
}

/** Snap a global ms to the nearest global word start within `threshold`. */
export function snapGlobal(ms: number, starts: number[], threshold = 140): number | null {
  let best: number | null = null;
  let bestDist = Infinity;
  for (const s of starts) {
    const d = Math.abs(s - ms);
    if (d <= threshold && d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  return best;
}

interface Timing {
  triggerMs?: number;
  endMs?: number;
  startWordIndex?: number;
}

function resolveStart(
  steps: RevealStep[],
  spans: StepSpan[],
  newGlobalMs: number,
): { stepIndex: number; localMs: number; wordIndex?: number } {
  const { index } = globalToStep(spans, newGlobalMs);
  const localRaw = newGlobalMs - spans[index].start;
  const snap = snapMsToWord(localRaw, steps[index]?.audio?.words, 'start');
  return {
    stepIndex: index,
    localMs: snap ? snap.ms : Math.max(0, Math.round(localRaw)),
    wordIndex: snap?.wordIndex,
  };
}

function ensureId(ids: string[] = [], id: string): string[] {
  return ids.includes(id) ? ids : [...ids, id];
}

const KEY: Record<Exclude<ItemKind, 'reveal'>, 'annotations' | 'cameraKeyframes' | 'drawStrokes' | 'spotlights'> =
  {
    annotation: 'annotations',
    camera: 'cameraKeyframes',
    draw: 'drawStrokes',
    spotlight: 'spotlights',
  };

/** Move a clip to a new global start, preserving its duration (clamped to its
 *  new segment). Returns the changed steps to persist. */
export function moveClip(
  steps: RevealStep[],
  clip: TimelineClip,
  kind: ItemKind,
  newGlobalStart: number,
  spans: StepSpan[],
): RevealStep[] {
  const { stepIndex: toIdx, localMs, wordIndex } = resolveStart(steps, spans, newGlobalStart);
  const fromIdx = clip.ownerStepIndex;
  const durationMs = clip.endMs != null ? clip.endMs - clip.startMs : null;
  const timing: Timing = { triggerMs: localMs > 0 ? localMs : undefined, startWordIndex: wordIndex };
  if (durationMs != null) {
    timing.endMs = Math.min(localMs + durationMs, stepDurationMs(steps[toIdx]));
  }
  if (kind === 'reveal') return moveReveal(steps, clip.refId, fromIdx, toIdx, timing);
  return moveObj(steps, KEY[kind], clip.refId, fromIdx, toIdx, timing);
}

/** Set a clip's out-time (resize). Stays in its owner step. */
export function resizeClip(
  steps: RevealStep[],
  clip: TimelineClip,
  kind: ItemKind,
  newGlobalEnd: number,
  spans: StepSpan[],
): RevealStep[] {
  if (kind === 'reveal') return []; // reveals appear-and-stay
  const k = clip.ownerStepIndex;
  const dur = stepDurationMs(steps[k]);
  const localStart = clip.startMs - spans[k].start;
  let localEnd = newGlobalEnd - spans[k].start;
  const snap = snapMsToWord(localEnd, steps[k]?.audio?.words, 'end');
  if (snap) localEnd = snap.ms;
  localEnd = Math.max(localStart + MIN_CLIP_MS, Math.min(localEnd, dur));
  const s = steps[k];
  const arr = ((s[KEY[kind]] ?? []) as unknown as Array<{ id: string }>).map((o) =>
    o.id === clip.refId ? { ...o, endMs: Math.round(localEnd), endWordIndex: snap?.wordIndex } : o,
  );
  return [{ ...s, [KEY[kind]]: arr } as RevealStep];
}

function moveReveal(
  steps: RevealStep[],
  elementId: string,
  fromIdx: number,
  toIdx: number,
  timing: Timing,
): RevealStep[] {
  if (fromIdx === toIdx && fromIdx >= 0) {
    const s = steps[toIdx];
    const et = { ...(s.elementTimings ?? {}) };
    et[elementId] = { triggerMs: timing.triggerMs, startWordIndex: timing.startWordIndex };
    return [{ ...s, elementIds: ensureId(s.elementIds, elementId), elementTimings: et }];
  }
  const updates: RevealStep[] = [];
  if (fromIdx >= 0) {
    const s = steps[fromIdx];
    const et = { ...(s.elementTimings ?? {}) };
    delete et[elementId];
    updates.push({
      ...s,
      elementIds: (s.elementIds ?? []).filter((x) => x !== elementId),
      elementTimings: et,
    });
  }
  const t = steps[toIdx];
  const et = { ...(t.elementTimings ?? {}) };
  et[elementId] = { triggerMs: timing.triggerMs, startWordIndex: timing.startWordIndex };
  updates.push({ ...t, elementIds: ensureId(t.elementIds, elementId), elementTimings: et });
  return updates;
}

function moveObj(
  steps: RevealStep[],
  key: 'annotations' | 'cameraKeyframes' | 'drawStrokes' | 'spotlights',
  refId: string,
  fromIdx: number,
  toIdx: number,
  timing: Timing,
): RevealStep[] {
  const src = fromIdx >= 0 ? steps[fromIdx] : null;
  const arr = (src?.[key] ?? []) as unknown as Array<Record<string, unknown> & { id: string }>;
  const obj = arr.find((o) => o.id === refId);
  if (!obj) return [];
  const updated = {
    ...obj,
    triggerMs: timing.triggerMs,
    startWordIndex: timing.startWordIndex,
    endMs: timing.endMs,
    // moving keeps the duration; the bound end word is no longer meaningful.
    endWordIndex: undefined,
  };
  if (fromIdx === toIdx) {
    return [{ ...(src as RevealStep), [key]: arr.map((o) => (o.id === refId ? updated : o)) } as RevealStep];
  }
  const updates: RevealStep[] = [];
  if (src) updates.push({ ...src, [key]: arr.filter((o) => o.id !== refId) } as RevealStep);
  const dst = steps[toIdx];
  const dstArr = (dst[key] ?? []) as Array<unknown>;
  updates.push({ ...dst, [key]: [...dstArr, updated] } as RevealStep);
  return updates;
}

/** Remove a clip's underlying item. */
export function removeClip(steps: RevealStep[], clip: TimelineClip, kind: ItemKind): RevealStep | null {
  const k = clip.ownerStepIndex;
  if (k < 0) return null;
  const s = steps[k];
  if (kind === 'reveal') {
    const et = { ...(s.elementTimings ?? {}) };
    delete et[clip.refId];
    return {
      ...s,
      elementIds: (s.elementIds ?? []).filter((x) => x !== clip.refId),
      elementTimings: et,
    };
  }
  const key = KEY[kind];
  const arr = (s[key] ?? []) as unknown as Array<{ id: string }>;
  return { ...s, [key]: arr.filter((o) => o.id !== clip.refId) } as RevealStep;
}

/** Bind a clip's start to a specific word in its (or the target) step. */
export function bindClipToWord(
  steps: RevealStep[],
  clip: TimelineClip,
  kind: ItemKind,
  spans: StepSpan[],
  stepIndex: number,
  wordIndex: number,
): RevealStep[] {
  const w = steps[stepIndex]?.audio?.words?.[wordIndex];
  if (!w) return [];
  return moveClip(steps, clip, kind, spans[stepIndex].start + Math.round(w.start * 1000), spans);
}
